import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import QRLinking from "@/components/linking/QRLinking";
import RouteLoadingShell from "@/components/shared/RouteLoadingShell";
import {
  captureShareLink,
  getAttributionCode,
  getCurrentAccountId,
  getPreferencesBootstrapDecision,
  primeLinkingSession,
  preloadExistingPreferencesRoute,
} from "@/lib/api";
import { shouldPrimeLinkRouteSession } from "@/lib/account-linking";
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import {
  resolveAuthenticatedSupabaseUser,
} from "@/lib/onboarding-auth";
import {
  clearPreferencesResumeTarget,
  getPreferencesSignInPath,
  readPreferencesResumeTargetFromSearch,
  resolvePostLinkPreferencesPath,
} from "@/lib/preferences-resume";
import {
  buildAuthenticatedPostLinkPreferencesPath,
  buildExistingPreferencesPath,
  buildOnboardingPreferencesPath,
} from "@/lib/preferences-route";
import { getLinkTestSourceFromSearch } from "@/lib/link-test-source";
import { getShareLinkEntry, rememberShareLinkEntry, shouldCaptureShareLinkForLinkPage } from "@/lib/share-link-entry";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";

export default function Link() {
  const location = useLocation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isRelink = searchParams.get("relink") === "1";
  const relinkAccountId = searchParams.get("accountId") || "";
  const testSource = getLinkTestSourceFromSearch(location.search);
  const preferencesIntent = readPreferencesResumeTargetFromSearch(location.search) === "preferences";

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      let willRedirectToPreferences = false;
      try {
        const referralCode = searchParams.get("ref") || "";
        if (
          shouldCaptureShareLinkForLinkPage({
            urlCode: referralCode,
            attributionCode: getAttributionCode(),
            shareLinkEntry: getShareLinkEntry(),
          })
        ) {
          const payload = await captureShareLink(referralCode, { track: false });
          if (cancelled) {
            return;
          }
          rememberShareLinkEntry(payload?.shareLink || null);
        }

        const client = getSupabaseBrowserClient();
        if (!client) {
          return;
        }
        const {
          data: { session },
        } = await client.auth.getSession();
        if (!session?.access_token) {
          return;
        }
        const user = await resolveAuthenticatedSupabaseUser(client, session);
        if (!user) {
          return;
        }
        const entryDecision = await getPreferencesBootstrapDecision();
        const entryDestination = String(entryDecision?.destination || "").trim().toLowerCase();
        if (!cancelled && !isRelink && ["preferences", "setup"].includes(entryDestination)) {
          willRedirectToPreferences = true;
          clearPreferencesResumeTarget();
          const buildPreferencesPath = entryDestination === "setup"
            ? buildOnboardingPreferencesPath
            : buildExistingPreferencesPath;
          const nextPath = buildPreferencesPath({
            forceLive: ONBOARDING_DESIGN_MODE,
            accountId: entryDecision?.accountId || "",
            selectedPlan: searchParams.get("selectedPlan") || "",
          });
          await Promise.all([
            preloadExistingPreferencesRoute(entryDecision?.accountId || "", {
              accountPayload: entryDecision?.accountPayload || null,
            }),
            import("@/pages/Setup"),
          ]);
          navigate(
            nextPath,
            { replace: true },
          );
          return;
        }
      } catch {
        // fall through to linking when the signed-in user does not own an account yet
      } finally {
        if (!cancelled) {
          if (shouldPrimeLinkRouteSession({ isRelink, willRedirectToPreferences })) {
            void primeLinkingSession({ testSource }).catch(() => {});
          }
          setReady(true);
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [isRelink, location.search, navigate]);

  async function handleLinkSuccess(success = {}) {
    if (isRelink) {
      navigate("/account?relinked=1", { replace: true });
      return;
    }

    const successAccountId = String(success?.accountId || "").trim();
    if (String(success?.setupMode || "").trim().toLowerCase() === "existing") {
      clearPreferencesResumeTarget();
      navigate(
        buildExistingPreferencesPath({
          forceLive: ONBOARDING_DESIGN_MODE,
          accountId: successAccountId,
        }),
        { replace: true },
      );
      return;
    }

    const client = getSupabaseBrowserClient();
    let hasAuthenticatedUser = false;

    if (client) {
      try {
        const {
          data: { session },
        } = await client.auth.getSession();
        if (session?.access_token) {
          const user = await resolveAuthenticatedSupabaseUser(client, session);
          hasAuthenticatedUser = Boolean(user);
        }
      } catch {
        hasAuthenticatedUser = false;
      }
    }

    const nextPath = resolvePostLinkPreferencesPath({
      search: location.search,
      hasAuthenticatedUser,
      forceLiveExistingPreferences: ONBOARDING_DESIGN_MODE,
    });
    if (hasAuthenticatedUser) {
      clearPreferencesResumeTarget();
      navigate(
        buildAuthenticatedPostLinkPreferencesPath({
          isRelink,
          forceLive: ONBOARDING_DESIGN_MODE,
          accountId: successAccountId || getCurrentAccountId(),
          selectedPlan: searchParams.get("selectedPlan") || "",
        }),
        { replace: true },
      );
      return;
    }
    if (nextPath) {
      clearPreferencesResumeTarget();
      navigate(nextPath, { replace: true });
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("context", "linked");
    navigate(`/auth?${nextSearchParams.toString()}`);
  }

  if (!ready) {
    return (
      <RouteLoadingShell
        title="Preparing your link…"
        copy="Checking whether this browser already has a linked Min account."
      />
    );
  }

  return (
    <QRLinking
      relinkAccountId={relinkAccountId}
      testSource={testSource}
      onSuccess={(success) => void handleLinkSuccess(success)}
      onBack={() => navigate(isRelink ? "/account" : "/")}
      onSignIn={() => navigate(
        isRelink
          ? `/auth${location.search}`
          : preferencesIntent
            ? getPreferencesSignInPath()
            : `/auth${location.search}`,
      )}
    />
  );
}
